import React, { useState, useEffect } from 'react';
import { 
  Stepper, Step, StepLabel, Button, Typography, Box, CircularProgress, 
  Paper, Grid, useTheme, Chip, Stack, Fade, IconButton, Collapse,
  List, ListItem, ListItemIcon, ListItemText, Divider, Alert,
  Card, CardContent, LinearProgress, Tooltip
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  Translate as TranslateIcon,
  FormatAlignLeft as FormatIcon,
  Psychology as PsychologyIcon,
  Hub as HubIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Groups as GroupsIcon,
  Analytics as AnalyticsIcon,
  EditNote as EditNoteIcon,
  SmartToy as SmartToyIcon,
  Security as SecurityIcon,
  CloudSync as CloudSyncIcon
} from '@mui/icons-material';

const steps = ['Profile Setup', 'Specialty & Interests', 'Workflow Preferences'];

export default function DoctorOnboarding() {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [expandedSection, setExpandedSection] = useState<string | null>('ai-tools');
  const [featureProgress, setFeatureProgress] = useState<{[key: string]: number}>({});

  // Core platform features
  const features = [
    "EmpowerAIᴺᴱ Engine",
    "NOEᴺᴱ Models",
    "AccessMatrixᴺᴱ",
    "TuneMatrixᴺᴱ"
  ];

  // AI-Enhanced Reporting Tools
  const aiReportingTools = [
    {
      icon: <AutoFixHighIcon />,
      title: "Inline Text Refinement",
      description: "Select any text for instant AI refinement",
      steps: [
        "Select text in the report editor",
        "Click the AI refinement popup",
        "Provide refinement instructions",
        "AI refines while maintaining medical accuracy"
      ],
      progress: 85
    },
    {
      icon: <FormatIcon />,
      title: "Auto-Format with AI",
      description: "One-click markdown formatting for entire reports",
      steps: [
        "Click the Auto-Format button",
        "LLM analyzes document structure",
        "Applies proper headings and lists",
        "Preserves medical terminology"
      ],
      progress: 90
    },
    {
      icon: <TranslateIcon />,
      title: "Multi-Language Translation",
      description: "OpenAI-powered medical translation",
      steps: [
        "Select target language (Greek, German, Spanish)",
        "Click translate button",
        "LLM translates preserving medical terms",
        "Maintains markdown formatting"
      ],
      progress: 95
    }
  ];

  // Knowledge Extraction & Analysis
  const knowledgeFeatures = [
    {
      icon: <PsychologyIcon />,
      title: "NOETIV Skinnerᴺᴱ Engine",
      description: "RoBERTa-based condition name extraction",
      details: "Automatically extracts dermatological conditions-tag them & bag them"
    },
    {
      icon: <HubIcon />,
      title: "NOETIV InsightGraphᴺᴱ Engine",
      description: "Interactive force-directed clinical relationships",
      details: "Visual representation of diagnoses, risks, and recommendations"
    },
    {
      icon: <TimelineIcon />,
      title: "NOETIV MetaSenseᴺᴱ Engine",
      description: "Sstructured output for clinical data",
      details: "Extracts color, size, shape, texture, risk factors automatically"
    }
  ];

  // Network & Collaboration Features
  const networkFeatures = [
    {
      icon: <GroupsIcon />,
      title: "Role-Based Access Control",
      roles: [
        { role: "Primary Physician", capabilities: ["Full case access", "Edit reports", "AI tools"] },
        { role: "Consulting Specialist", capabilities: ["View cases", "Add recommendations", "Export data"] },
        { role: "Medical Assistant", capabilities: ["Data entry", "Follow-up scheduling", "Basic reports"] },
        { role: "Administrator", capabilities: ["User management", "Analytics access", "System configuration"] }
      ]
    },
    {
      icon: <CloudSyncIcon />,
      title: "Case Sharing & Collaboration",
      features: ["Secure case sharing", "Real-time collaboration", "Comment threads", "Version control"]
    },
    {
      icon: <SecurityIcon />,
      title: "HIPAA-Compliant Communication",
      features: ["End-to-end encryption", "Audit trails", "Secure messaging", "Data retention policies"]
    }
  ];

  // AI Customization Tools
  const customizationTools = [
    {
      title: "Workflow Automation Levels",
      options: ["Manual review", "AI suggestions", "Semi-automated", "Fully automated"],
      icon: <SpeedIcon />
    },
    {
      title: "Custom Prompt Templates",
      options: ["Save refinement prompts", "Create specialty templates", "Share with team"],
      icon: <EditNoteIcon />
    },
    {
      title: "AI Model Selection",
      options: ["GPTs for analysis", "NOEᴺᴱ for reports", "Custom fine-tuning"],
      icon: <SmartToyIcon />
    }
  ];

  // Simulate development progress
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => 
        prevProgress >= 100 ? 0 : prevProgress + 1
      );
    }, 800);
    
    const featureTimer = setInterval(() => {
      setActiveFeature((prevFeature) => 
        prevFeature >= features.length - 1 ? 0 : prevFeature + 1
      );
    }, 3000);

    // Animate feature progress bars
    const progressTimer = setInterval(() => {
      setFeatureProgress(prev => {
        const newProgress = { ...prev };
        aiReportingTools.forEach((tool, idx) => {
          const key = `tool-${idx}`;
          newProgress[key] = Math.min((newProgress[key] || 0) + Math.random() * 10, tool.progress);
        });
        return newProgress;
      });
    }, 1000);
    
    return () => {
      clearInterval(timer);
      clearInterval(featureTimer);
      clearInterval(progressTimer);
    };
  }, []);

  const handleExpandSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return(
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      minHeight="100vh"
      sx={{
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
          : 'linear-gradient(135deg, #1e2329 0%, #121a24 100%)',
        padding: 4,
        overflowY: 'auto'
      }}
    >
      {/* Main Overview Card */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 5, 
          borderRadius: 4, 
          maxWidth: 1200,
          width: '100%',
          mb: 3,
          background: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.9)'
            : 'rgba(32, 38, 45, 0.8)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${theme.palette.mode === 'light'
            ? 'rgba(209, 213, 219, 0.3)'
            : 'rgba(76, 86, 106, 0.3)'}`
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={4} textAlign="center">
            <Box position="relative" display="inline-flex">
              <CircularProgress 
                variant="determinate" 
                value={progress} 
                size={180}
                thickness={4}
                sx={{ color: theme.palette.primary.main }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                  {`${Math.round(progress)}%`}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
              AI-Powered Medical Platform
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Comprehensive onboarding with advanced AI tools
            </Typography>
            
            <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 3 }} flexWrap="wrap">
              <Chip label=" MultiLinguaReportᴺᴱ System" color="primary" variant="filled" />
              <Chip label=" MetaSenseᴺᴱ Engine" color="secondary" variant="filled" />
              {/* <Chip label="Multi-Role Support" color="success" variant="filled" /> */}
              {/* <Chip label="AccessMatrixᴺᴱ" color="info" variant="filled" /> */}
            </Stack>
            
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Currently developing:
              </Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  mt: 1, 
                  border: `1px solid ${theme.palette.divider}`,
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  borderRadius: 1,
                  bgcolor: theme.palette.mode === 'light'
                    ? 'rgba(63, 81, 181, 0.05)'
                    : 'rgba(63, 81, 181, 0.15)'
                }}
              >
                <Fade in key={activeFeature}>
                  <Typography variant="body1">
                    {features[activeFeature]}
                  </Typography>
                </Fade>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Stepper activeStep={-1} alternativeLabel sx={{ mt: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* AI-Enhanced Reporting Tools Section */}
      <Card sx={{ width: '100%', maxWidth: 1200, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold" color="primary">
              <AutoFixHighIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              MultiLinguaReportᴺᴱ
            </Typography>
            <IconButton onClick={() => handleExpandSection('ai-tools')}>
              {expandedSection === 'ai-tools' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSection === 'ai-tools'}>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {aiReportingTools.map((tool, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      {tool.icon}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {tool.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {tool.description}
                    </Typography>
                    
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Step-by-step:
                    </Typography>
                    <List dense>
                      {tool.steps.map((step, stepIdx) => (
                        <ListItem key={stepIdx}>
                          <ListItemIcon>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText primary={step} />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={featureProgress[`tool-${idx}`] || 0}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Knowledge Extraction Features */}
      <Card sx={{ width: '100%', maxWidth: 1200, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold" color="primary">
              <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              NOETIV InsightGraphᴺᴱ & Skinnerᴺᴱ
            </Typography>
            <IconButton onClick={() => handleExpandSection('knowledge')}>
              {expandedSection === 'knowledge' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSection === 'knowledge'}>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {knowledgeFeatures.map((feature, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      {feature.icon}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {feature.description}
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {feature.details}
                    </Alert>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Network & Collaboration */}
      <Card sx={{ width: '100%', maxWidth: 1200, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold" color="primary">
              <GroupsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              NOETIV AccessMatrixᴺᴱ Engine
            </Typography>
            <IconButton onClick={() => handleExpandSection('network')}>
              {expandedSection === 'network' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSection === 'network'}>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {networkFeatures.map((feature, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      {feature.icon}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    
                    {feature.roles && (
                      <>
                        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                          Supported Roles:
                        </Typography>
                        {feature.roles.map((roleInfo, roleIdx) => (
                          <Box key={roleIdx} sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {roleInfo.role}
                            </Typography>
                            {roleInfo.capabilities.map((cap, capIdx) => (
                              <Typography key={capIdx} variant="caption" display="block" sx={{ ml: 2 }}>
                                • {cap}
                              </Typography>
                            ))}
                          </Box>
                        ))}
                      </>
                    )}
                    
                    {feature.features && (
                      <List dense>
                        {feature.features.map((item, itemIdx) => (
                          <ListItem key={itemIdx}>
                            <ListItemIcon>
                              <CheckCircleIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={item} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* AI Customization Tools */}
      <Card sx={{ width: '100%', maxWidth: 1200, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold" color="primary">
              <SmartToyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              NOETIV EmpowerAIᴺᴱ Engine
            </Typography>
            <IconButton onClick={() => handleExpandSection('customization')}>
              {expandedSection === 'customization' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={expandedSection === 'customization'}>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {customizationTools.map((tool, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      {tool.icon}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {tool.title}
                      </Typography>
                    </Box>
                    <List dense>
                      {tool.options.map((option, optIdx) => (
                        <ListItem key={optIdx}>
                          <ListItemText 
                            primary={option}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Paper sx={{ p: 3, maxWidth: 1200, width: '100%' }}>
        <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
          Development Timeline
        </Typography>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center">
            <Chip label="Q3 2026" color="success" />
            <Typography variant="body2" sx={{ ml: 2 }}>
              EmpowerAIᴺᴱ Engine - Complete
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Chip label="Q1 2026" color="info" />
            <Typography variant="body2" sx={{ ml: 2 }}>
              AccessMatrixᴺᴱ Engine
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Chip label="Q2 2026" color="default" />
            <Typography variant="body2" sx={{ ml: 2 }}>
              TuneMatrixᴺᴱ Engine
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}